<template>
  <div class="index py-5">
    <div class="d-flex align-center">
      <span class="d-flex flex-row page-title">{{$t("intranet.title.page.featuredRegion.list")}}</span>
      <v-spacer></v-spacer>
      <v-btn depressed color="primary" @click="edit(null)">
        {{$t("intranet.homeContent.button.createFeaturedRegion")}}
      </v-btn>
    </div>
    <p class="text-body-2 mt-2">{{$t("intranet.homeContent.featuredRegion.intro")}}</p>

    <div class="searchable-list-container">
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        disable-pagination
        hide-default-footer
      >
        <template v-slot:item.position="{ index }">{{ index + 1 }}</template>
        <template v-slot:item.label="{ item }">{{ displayed(item) }}</template>
        <template v-slot:item.region="{ item }">
          <span v-if="item.region">{{ describeRegion(item.region) }}</span>
          <v-chip v-else small outlined>{{ $t('intranet.homeContent.featuredRegion.other') }}</v-chip>
        </template>
        <template v-slot:item.published="{ item }">
          <v-chip small :color="item.published ? 'success' : 'grey lighten-1'">
            {{ item.published ? $t('intranet.homeContent.published') : $t('intranet.homeContent.draft') }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item, index }">
          <v-btn icon :disabled="moving || index === 0" :title="$t('intranet.homeContent.featuredRegion.moveUp')" @click="move(index, -1)">
            <v-icon>mdi-arrow-up</v-icon>
          </v-btn>
          <v-btn icon :disabled="moving || index === items.length - 1" :title="$t('intranet.homeContent.featuredRegion.moveDown')" @click="move(index, 1)">
            <v-icon>mdi-arrow-down</v-icon>
          </v-btn>
          <v-btn icon @click="edit(item)">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon color="error" @click="askDelete(item)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </div>

    <!-- Creation et modification : quatre champs, un dialogue suffit. -->
    <v-dialog v-model="formDialog" max-width="640" :persistent="saving">
      <v-card v-if="draft">
        <v-toolbar class="pl-6 text-h6" color="primary" dark flat>
          {{ draft.id ? $t('intranet.title.page.featuredRegion.update') : $t('intranet.title.page.featuredRegion.create') }}
        </v-toolbar>
        <v-card-text class="pa-6">
          <v-form ref="form" v-model="valid">
            <AveineRegionPicker
              v-model="region"
              :label="$t('intranet.homeContent.featuredRegion.region')"
              :hint="$t('intranet.homeContent.featuredRegion.regionHint')"
              :APIbaseURL="APIbaseURL"
            />
            <!-- Sans region, le libelle est tout ce que l'app affiche : fr et
                 en deviennent obligatoires, comme CRUD-Wine l'exige. -->
            <AveineTranslatedField
              :key="region ? 'optional' : 'required'"
              class="mt-4"
              :value="draft.label"
              :label="$t('intranet.homeContent.featuredRegion.label')"
              :required="region ? [] : ['fr', 'en']"
            />
            <p v-if="region" class="text-caption mt-n2">{{ $t('intranet.homeContent.featuredRegion.labelHint') }}</p>
            <v-switch v-model="draft.published" :label="$t('intranet.homeContent.attributes.published')" />
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text :disabled="saving" @click="formDialog = false">{{$t('intranet.referential.button.cancel')}}</v-btn>
          <v-btn depressed color="primary" :loading="saving" :disabled="!valid" @click="save()">
            {{$t('intranet.referential.button.save')}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="480">
      <v-card>
        <v-card-title>{{$t('intranet.homeContent.featuredRegion.deleteTitle')}}</v-card-title>
        <v-card-text>{{$t('intranet.homeContent.featuredRegion.deleteConfirmation')}}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">{{$t('intranet.referential.button.cancel')}}</v-btn>
          <v-btn color="error" text @click="confirmDelete()">{{$t('intranet.referential.button.delete')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FeaturedRegion from '@/store/models/featured-region'
import AveineRegionPicker from '@/components/AveineRegionPicker.vue'
import AveineTranslatedField from '@/components/AveineTranslatedField.vue'
import { errorHandler } from 'aveine-toolbox'

/**
 * Les regions proposees dans le profil de gout de l'app (REDMINE-13656).
 *
 * La liste est courte (une dizaine d'entrees) : une seule page, dans l'ordre
 * d'affichage de l'app, reordonnee par les fleches. Une entree sans region est
 * « Autres regions & vins du monde ».
 */
export default {
  name: 'Index',
  components: { AveineRegionPicker, AveineTranslatedField },
  data () {
    return {
      items: [],
      loading: false,
      moving: false,
      formDialog: false,
      draft: null,
      region: null,
      valid: false,
      saving: false,
      deleteDialog: false,
      itemToDelete: null,
      APIbaseURL: process.env.VUE_APP_API_HOST
    }
  },
  computed: {
    headers () {
      return [
        { text: '#', value: 'position', sortable: false, width: 60 },
        { text: this.$t('intranet.homeContent.featuredRegion.displayed'), value: 'label', sortable: false },
        { text: this.$t('intranet.homeContent.featuredRegion.region'), value: 'region', sortable: false },
        { text: this.$t('intranet.homeContent.attributes.published'), value: 'published', sortable: false, width: 140 },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 220 }
      ]
    }
  },
  mounted () {
    this.fetch()
  },
  methods: {
    translate (translation) {
      if (!translation) return ''
      if (typeof translation === 'string') return translation
      return translation[this.$i18n.locale] || translation.fr || translation.en || ''
    },

    describeRegion (region) {
      return AveineRegionPicker.describe(region, this.$i18n.locale)
    },

    /** Ce que l'app affiche : le libelle s'il est rempli, sinon le nom de la region. */
    displayed (item) {
      return this.translate(item.label) || (item.region ? this.translate(item.region.name) : '') || '—'
    },

    async fetch () {
      this.loading = true
      try {
        const response = await FeaturedRegion.api().get(
          `featured-regions?lang=${this.$i18n.locale}`,
          {
            baseURL: this.APIbaseURL,
            params: { include: 'region.country', sort: 'position', 'page[size]': 100 }
          }
        )
        // Restreint aux identifiants recus, dans l'ordre du serveur : voir
        // wines-of-the-month/Index.vue.
        const ids = response.entities ? (response.entities.featured_regions || []).map(item => item.id) : []
        const byId = new Map(
          FeaturedRegion.query().whereIdIn(ids).withAllRecursive().get().map(item => [item.id, item])
        )
        this.items = ids.map(id => byId.get(id)).filter(Boolean)
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.loading = false
      }
    },

    edit (item) {
      this.draft = item
        ? { id: item.id, label: { ...(item.label || {}) }, published: item.published }
        : { id: null, label: {}, published: false }
      this.region = item && item.region
        ? { id: item.region.id, label: this.describeRegion(item.region) }
        : null
      this.formDialog = true
    },

    async save () {
      this.saving = true
      try {
        // `$entity_name` sur la ressource et sur le lien : c'est de la que le
        // dataTree tire les types JSON:API (voir AveineWineOfTheMonthForm).
        const payload = {
          $entity_name: FeaturedRegion.entity,
          label: this.draft.label,
          published: this.draft.published,
          region: this.region ? { $entity_name: 'regions', id: this.region.id } : null,
          region_id: this.region ? this.region.id : null
        }
        if (this.draft.id) {
          await FeaturedRegion.api().patch(
            `featured-regions/${this.draft.id}?lang=${this.$i18n.locale}`,
            { ...payload, id: this.draft.id },
            { baseURL: this.APIbaseURL, save: false }
          )
        } else {
          // En fin de liste.
          const last = this.items.reduce((max, item) => Math.max(max, item.position || 0), 0)
          await FeaturedRegion.api().post(
            `featured-regions?lang=${this.$i18n.locale}`,
            { ...payload, position: last + 1 },
            { baseURL: this.APIbaseURL, save: false }
          )
        }
        this.formDialog = false
        this.$toastr.s(this.$t('intranet.referential.title.saveConfirmation'))
        await this.fetch()
      } catch (errors) {
        if (this.isRegionTaken(errors)) {
          this.$toastr.e(this.$t('intranet.homeContent.featuredRegion.regionTaken'))
        } else {
          errorHandler(errors, this)
        }
      } finally {
        this.saving = false
      }
    },

    /** Le refus de CRUD-Wine quand la region est deja proposee. */
    isRegionTaken (errors) {
      const body = errors && errors.response && errors.response.data
      if (!body || !Array.isArray(body.errors)) return false
      return body.errors.some(error => /already featured|featured_regions_region_unique/.test(error.detail || ''))
    },

    /**
     * Echange deux entrees voisines. Les positions sont renumerotees de 1 a n
     * au passage : une liste dont les positions se suivent mal (suppressions,
     * ex aequo) se remet d'aplomb au premier deplacement.
     */
    async move (index, step) {
      const order = this.items.slice()
      const [item] = order.splice(index, 1)
      order.splice(index + step, 0, item)

      this.moving = true
      try {
        for (const [position, entry] of order.entries()) {
          if (entry.position === position + 1) continue
          await FeaturedRegion.api().patch(
            `featured-regions/${entry.id}?lang=${this.$i18n.locale}`,
            { $entity_name: FeaturedRegion.entity, id: entry.id, position: position + 1 },
            { baseURL: this.APIbaseURL, save: false }
          )
        }
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.moving = false
        await this.fetch()
      }
    },

    askDelete (item) {
      this.itemToDelete = item
      this.deleteDialog = true
    },

    async confirmDelete () {
      try {
        await FeaturedRegion.api().delete(
          `featured-regions/${this.itemToDelete.id}`,
          { baseURL: this.APIbaseURL, save: false }
        )
        FeaturedRegion.delete(this.itemToDelete.id)
        this.deleteDialog = false
        this.itemToDelete = null
        await this.fetch()
      } catch (errors) {
        this.deleteDialog = false
        errorHandler(errors, this)
      }
    }
  }
}
</script>
